import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from '@/lib/toast'
import { twoFactorApi } from '@/api/endpoints'
import { SectionSkeleton, SectionError } from './SectionState'
import { profileQuery } from './prefetch'

// ============================================================================
// Two-factor authentication (super admin)
// ============================================================================

export function TwoFactorSection() {
  const { t } = useTranslation('settings')
  const { t: tc } = useTranslation('common')
  const queryClient = useQueryClient()
  const { data: profile, isPending, isError, refetch } = useQuery(profileQuery)

  const [phone, setPhone] = useState('')
  const [enrollCode, setEnrollCode] = useState('')
  const [awaitingCode, setAwaitingCode] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [password, setPassword] = useState('')

  const invalidate = () => queryClient.invalidateQueries({ queryKey: profileQuery.queryKey })

  const startMut = useMutation({
    mutationFn: () => twoFactorApi.phoneEnrollStart(phone.trim()),
    onSuccess: () => { setAwaitingCode(true); toast.success(t('twoFactor.codeSent')) },
    onError: (e: unknown) => {
      const s = (e as { response?: { status?: number } }).response?.status
      toast.error(s === 400 ? t('twoFactor.invalidPhone') : t('twoFactor.genericError'))
    },
  })
  const verifyMut = useMutation({
    mutationFn: () => twoFactorApi.phoneEnrollVerify(enrollCode.trim()),
    onSuccess: () => { setAwaitingCode(false); setPhone(''); setEnrollCode(''); invalidate(); toast.success(t('twoFactor.enabledToast')) },
    onError: () => toast.error(t('twoFactor.wrongCode')),
  })
  const disableMut = useMutation({
    mutationFn: () => twoFactorApi.phoneDisable(password),
    onSuccess: () => { setRemoving(false); setPassword(''); invalidate(); toast.success(t('twoFactor.disabledToast')) },
    onError: (e: unknown) => {
      const s = (e as { response?: { status?: number } }).response?.status
      toast.error(s === 400 ? t('twoFactor.wrongPassword') : t('twoFactor.genericError'))
    },
  })

  const on = !!profile?.phone_verified
  const last4 = (profile?.phone_number || '').slice(-4)

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-base font-semibold">{t('twoFactor.title')}</h2>
        <p className="text-xs text-muted-foreground">{t('twoFactor.description')}</p>
      </div>
      {isPending ? (
        <SectionSkeleton rows={1} />
      ) : isError && !profile ? (
        <SectionError onRetry={() => refetch()} />
      ) : (
        <div className="rounded-lg border bg-card p-4 shadow-sm space-y-3">
          {on ? (
            <>
              <p className="text-sm font-medium">{t('twoFactor.onTitle')}</p>
              <p className="text-xs text-muted-foreground">{t('twoFactor.phoneEnding', { last4 })}</p>
              {!removing ? (
                <Button variant="outline" size="sm" onClick={() => setRemoving(true)}>{t('twoFactor.remove')}</Button>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">{t('twoFactor.removeHint')}</p>
                  <Input type="password" autoComplete="current-password" placeholder={t('twoFactor.passwordPlaceholder')}
                         value={password} onChange={(e) => setPassword(e.target.value)} />
                  <div className="flex gap-2">
                    <Button variant="destructive" size="sm" disabled={disableMut.isPending || !password}
                            onClick={() => disableMut.mutate()}>
                      {disableMut.isPending ? t('twoFactor.removing') : t('twoFactor.confirmRemove')}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setRemoving(false); setPassword('') }}>
                      {tc('actions.cancel')}
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <p className="text-sm font-medium">{t('twoFactor.offTitle')}</p>
              <p className="text-xs text-muted-foreground">{t('twoFactor.offHint')}</p>
              {!awaitingCode ? (
                <div className="space-y-2">
                  <Input type="tel" placeholder={t('twoFactor.phonePlaceholder')} value={phone}
                         onChange={(e) => setPhone(e.target.value)} />
                  <Button size="sm" disabled={startMut.isPending || !phone.trim()} onClick={() => startMut.mutate()}>
                    {startMut.isPending ? t('twoFactor.sending') : t('twoFactor.sendCode')}
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Input type="text" inputMode="numeric" autoComplete="one-time-code"
                         placeholder={t('twoFactor.codePlaceholder')} value={enrollCode}
                         onChange={(e) => setEnrollCode(e.target.value)} />
                  <Button size="sm" disabled={verifyMut.isPending || !enrollCode.trim()} onClick={() => verifyMut.mutate()}>
                    {verifyMut.isPending ? t('twoFactor.confirming') : t('twoFactor.confirm')}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  )
}
